import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { downloadAuthedBlob } from '../../hooks/useAuthedBlobUrl';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { ShareRow, exportDownloadPath } from '../../services/stabledocsService';

const STATUS_LABELS: Record<string, string> = {
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  bounced: 'Bounced',
  failed: 'Failed',
};

export const deliveryLabel = (status: string) => STATUS_LABELS[status] ?? status;

/** A bounced or failed email: the sender needs to see it without opening the entry. */
export const deliveryProblem = (status: string) => status === 'bounced' || status === 'failed';

export const DeliveryChip: React.FC<{ status: string }> = ({ status }) => (
  <Chip
    size="small"
    label={deliveryLabel(status)}
    color={deliveryProblem(status) ? 'error' : 'default'}
    sx={{ height: 20, fontSize: '0.7rem' }}
  />
);

/** Body of a "StableDocs sent" timeline entry: who, when, what, and the exact PDF again. */
const ShareSentDetail: React.FC<{ share: ShareRow }> = ({ share }) => {
  const formatInstant = useFormatInstant();
  const { enqueueSnackbar } = useSnackbar();
  const to = share.recipientName ? `${share.recipientName} (${share.recipientEmail})` : share.recipientEmail;

  return (
    <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="body2">To {to}</Typography>
      <Typography variant="caption" color="text.secondary">
        {formatInstant(share.sentAt)}
        {share.senderName ? ` by ${share.senderName}` : ''} · {share.itemCount} {share.itemCount === 1 ? 'item' : 'items'}
        {' · Attachment'}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <DeliveryChip status={share.deliveryStatus} />
        <Button
          size="small"
          sx={{ textTransform: 'none' }}
          onClick={(e) => {
            e.stopPropagation();
            downloadAuthedBlob(exportDownloadPath(share.exportId), share.fileName || 'records.pdf').catch(() =>
              enqueueSnackbar('Could not download the PDF', { variant: 'error' }),
            );
          }}
        >
          Download PDF
        </Button>
      </Box>
    </Box>
  );
};

export default ShareSentDetail;
